import React, {memo, useCallback, useEffect, useMemo, useState} from 'react'
import {createTable} from '@tanstack/react-table'
import {useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {UpdateListType} from '../../component/type'
import {Button, List as ListView, Popup, Sort} from '../../component'
import ListRight from '../../component/listRight'
import {
  getClubList,
  getClubDetail,
  clearClubDetail,
  deleteClub,
  clearLeagueDetail,
} from '../../store/actions'
import {useSelector, useAppDispatch} from '../../store'
import {ClubType} from '../../store/actions/types'
import {EditeButtonList, RemoveButtonList} from '../../component/List/Commands'
import {optionList, clubRequestStatus} from '../../data'
import {defaultPageSize} from '../../configs/paginationSetting'
import images from '../../assets/images'

const table = createTable().setRowType<ClubType>()

const ClubManagement = memo(() => {
  const {t} = useTranslation()
  const navigation = useNavigate()
  const {list, total, from} = useSelector(({club}) => ({
    list: club.clubList,
    total: club.total,
    from: club.from,
  }))
  const [selectedRow, setSelectedRow] = useState<ClubType>()
  const [isOpenPopup, setIsOpenPopup] = useState(false)
  const [currentItemId, setCurrentItemId] = useState(0)
  const [loading, setLoading] = useState(false)
  const defaultOptions = useMemo(
    () => ({
      request_status: 0,
      page: 1,
      per_page: defaultPageSize,
    }),
    [],
  )
  const [options, setOptions] = useState(defaultOptions)
  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(getClubList(options))
  }, [options])

  const columns = useMemo(
    () => [
      table.createDataColumn('id', {
        header: 'NUMBER',
        size: 200,
        cell: ({row}) => <span>{String(row.index + from).padStart(2, '0')}</span>,
      }),
      table.createDataColumn('club_name', {
        enableResizing: true,
        header: 'CLUB NAME',
        size: 600,
        cell: ({row}) => (
          <div>
            {(row.original?.medias_model &&
              row.original.medias_model?.length > 0 &&
              row.original.medias_model[0] && (
                <img
                  src={row.original?.medias_model[0]?.original_url}
                  alt=""
                  className="img-testimonials-list mr-2"
                />
              )) || <img src={images.dummyUser} alt="" className="img-testimonials-list mr-2" />}
            <span>{row.original?.club_name}</span>
          </div>
        ),
      }),
      table.createDataColumn('request_status', {
        header: 'ClAIM STATUS',
        size: 400,
        cell: ({row}) => <span>{clubRequestStatus[row.original?.request_status || 0]}</span>,
      }),
      table.createDataColumn('id', {
        header: '',
        cell: ({row}) =>
          row.original && (
            <ListRight
              row={row.original}
              onClick={onUpdatePress}
              buttonList={[EditeButtonList, RemoveButtonList]}
            />
          ),
      }),
    ],
    [from],
  )

  const onSortChange = useCallback(
    (option: string) => {
      if (option === 'all') {
        setOptions({...options, request_status: 0, page: 1})
      } else if (option === 'pending') {
        setOptions({...options, request_status: 1, page: 1})
      } else if (option === 'requested') {
        setOptions({...options, request_status: 2, page: 1})
      } else if (option === 'approved') {
        setOptions({...options, request_status: 3, page: 1})
      } else if (option === 'denied') {
        setOptions({...options, request_status: 4, page: 1})
      }
    },
    [options],
  )
  const onUpdatePress = useCallback(
    async ({id, command}: UpdateListType) => {
      if (command === 'edit') {
        const response = await dispatch(getClubDetail(id))
        if (response) navigation(`/club-management/${id}`)
      } else if (command === 'remove') {
        setCurrentItemId(id)
        setIsOpenPopup(true)
      } else if (command === 'deleteConfirmed') {
        setLoading(true)
        await dispatch(deleteClub(id, options))
        setIsOpenPopup(false)
        setLoading(false)
      }
      setSelectedRow(undefined)
    },
    [selectedRow, list],
  )

  const onPaginate = useCallback(
    (page: number) => {
      setOptions({...options, page})
    },
    [options],
  )
  const onLimit = useCallback(
    (per_page: number) => {
      setOptions({...options, per_page, page: 1})
    },
    [options],
  )
  const onAddNewPress = useCallback(() => {
    dispatch(clearClubDetail())
    dispatch(clearLeagueDetail())
    navigation(`/club-management/add`)
  }, [])

  return (
    <div className="table-outer">
      <div className="table-header">
        <div className="c-carousal__header">
          <div className="c-carousal__header__lpart v-center ">
            <span className="f-Exo-Bold title-color">{t('totalClubList')}</span>
          </div>
          <div className="c-carousal__header__rpart flex-center">
            <Button
              icon="c-icon--plus"
              title={t('addNewClub')}
              classAddon="c-button--we-160 c-button--icons f-Exo-Medium"
              onClick={onAddNewPress}
            />
            <Popup
              isOpen={isOpenPopup}
              setIsOpen={setIsOpenPopup}
              onPressDelete={() => onUpdatePress({id: currentItemId, command: 'deleteConfirmed'})}
              title={t('delete')}
              description={t('deleteText')}
              loading={loading}
            />
            <div>
              <Sort optionList={optionList} onSortChange={onSortChange} classAddon="p-2" />
            </div>
          </div>
        </div>
      </div>
      <ListView
        columns={columns}
        data={list}
        total={total}
        onPaginate={onPaginate}
        forcePageIndex={options.page - 1}
        options={options}
        isPageSize
        onLimit={onLimit}
        pageSize={options?.per_page}
      />
    </div>
  )
})

export default ClubManagement
